import axios from 'axios';
import chunk from 'lodash/chunk';
import uniq from 'lodash/uniq';
import moment from 'moment-timezone';

import { PAGE_SIZE } from 'constants/list';
import { AcknowledgeNotificationsResponseDto } from 'types/dto/notification.dto';
import HttpStatusCode from 'types/http-status-code.enum';
import { EnterpriseId } from 'types/models/enterprise';
import Notification, {
  IPCameraEvent,
  NotificationEventType,
  NotificationId,
  NotificationStatus,
  NotificationType,
  SamplePointEvent
} from 'types/models/notification';
import { SiteId } from 'types/models/site';
import { UserId } from 'types/models/user';
import { APIErrorParsed, APIResponseParsed, PaginatedResponse } from 'types/response';
import { getRequest, patchRequest, putRequest } from 'utils/HttpRequest/http-request-utils';

import { parseAxiosError } from './utils/parse-axios-error';

// ==============================
// TYPES
// ==============================
type EventAcknowledgementId = number;

type EventAcknowledgement<
  E extends SamplePointEvent | IPCameraEvent = SamplePointEvent | IPCameraEvent
> = {
  id: EventAcknowledgementId; // This is to emphasize that the `id` field is from EventAcknowledgement resource from BE
  acknowledgedByUserId: UserId | null;
  acknowledgedByUser: {
    firstName: string | null;
    lastName: string | null;
  }
  acknowledgedAt: number | null;
  event: E;
  eventType: NotificationEventType;
  message: string;
};

// ==============================
// CONSTANTS
// ==============================

const BULK_ACKNOWLEDGE_BATCH_SIZE = 50;

// ==============================
// HELPERS
// ==============================

function toNotification(n: EventAcknowledgement): Notification {
  return {
    id: n.id,
    event: n.event,
    eventId: n.event.id,
    eventType: n.eventType,
    message: n.message,
    status: n.acknowledgedAt ? NotificationStatus.ACKNOWLEDGED : NotificationStatus.SENT,
    type: NotificationType.UI,
    acknowledgement: n.acknowledgedAt
      ? {
        user: {
          id: n.acknowledgedByUserId ?? 0,
          name: [n.acknowledgedByUser.firstName, n.acknowledgedByUser.lastName].filter(Boolean).join(' ')
        },
        at: n.acknowledgedAt
      }
      : null
  };
}

// ==============================
// REQUESTS
// ==============================
async function fetchPaginatedNotifications(
  enterpriseId: EnterpriseId,
  siteId: SiteId,
  page: number
): Promise<APIResponseParsed<PaginatedResponse<Notification>>> {
  try {
    const params = {
      enterpriseId,
      siteId,
      page,
      pageSize: PAGE_SIZE.NOTIFICATIONS
    };
    const { data } = await getRequest<PaginatedResponse<EventAcknowledgement>>(
      'notifications/v3',
      { params }
    );
    return {
      data: {
        ...data,
        data: data.data.map(toNotification)
      },
      error: null
    };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function fetch7DayUnacknowledgedNotifications(
  enterpriseId: EnterpriseId
): Promise<APIResponseParsed<Notification[]>> {
  try {
    const params = {
      enterpriseId,
      eventAcknowledged: false,
      fromDate: moment().utc().subtract(7, 'day').format(),
      page: 1,
      pageSize: 99999 // Any large number to fetch all unacknowledged notifications
    };
    const { data } = await getRequest<PaginatedResponse<EventAcknowledgement>>(
      'notifications/v3',
      { params }
    );
    return {
      data: data.data.map(toNotification),
      error: null
    };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function acknowledgeNotification(
  notificationId: NotificationId
): Promise<APIResponseParsed<Notification>> {
  const eventAcknowledgementId: EventAcknowledgementId = notificationId;
  try {
    const response = await patchRequest<EventAcknowledgement, { status: NotificationStatus }>(
      `notifications/v3/${eventAcknowledgementId}`,
      { status: NotificationStatus.ACKNOWLEDGED }
    );
    return { data: toNotification(response.data), error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

async function acknowledgeNotifications(
  notificationIds: NotificationId[]
): Promise<APIResponseParsed<AcknowledgeNotificationsResponseDto>> {
  const eventAcknowledgementIds: EventAcknowledgementId[] = notificationIds;
  const chunkedEventAcknowledgementIds = chunk(uniq(eventAcknowledgementIds), BULK_ACKNOWLEDGE_BATCH_SIZE);

  let accumulatedAcknowledged = 0;
  let accumulatedFailed = 0;
  const errors: APIErrorParsed[] = [];

  // Executes requests sequentially. BE has requested that we avoid parallelizing the requests to this endpoint
  // (e.g., Promise.all()) to prevent resource exhaustion.
  let isInIrecoverrableState = false;
  for (const ids of chunkedEventAcknowledgementIds) {
    if (isInIrecoverrableState) {
      accumulatedFailed += ids.length;
      continue;
    }

    try {
      const response = await putRequest<
        AcknowledgeNotificationsResponseDto,
        { eventAcknowledgementIds: EventAcknowledgementId[] }
      >(
        'notifications/v3/bulk-acknowledge',
        { eventAcknowledgementIds: ids }
      );
      accumulatedAcknowledged += response.data.acknowledged;
      accumulatedFailed += response.data.failed;
    } catch (error) {
      accumulatedFailed += ids.length;
      if (!axios.isAxiosError(error)) throw error;

      const parsedError = parseAxiosError(error);
      // Fail fast if a single operation encounters an irecoverrable error
      if (parsedError.isNetworkError || parsedError.httpStatusCode === HttpStatusCode.UNAUTHORIZED) {
        isInIrecoverrableState = true;
      }

      errors.push(parsedError);
    }
  }

  // If zero notifications were acknowledged and an error occurred,
  // treat the entire operation as failed and return only the first error.
  if (
    accumulatedAcknowledged === 0 &&
    errors.length > 0
  ) {
    return {
      data: null,
      error: errors[0]
    };
  }

  // If at least one notification were acknowledged successfully,
  // treat the operation as successful despite any subsequent failures.
  // Return only the actual counts to reflect what was completed.
  return {
    data: {
      acknowledged: accumulatedAcknowledged,
      failed: accumulatedFailed
    },
    error: null
  };
}

async function exportNotifications(
  enterpriseId: EnterpriseId,
  siteId: SiteId,
  startDate: moment.Moment,
  endDate: moment.Moment
): Promise<APIResponseParsed<string>> {
  try {
    const params = {
      enterpriseId,
      siteId,
      fromDate: startDate.utc().format(),
      endDate: endDate.utc().format()
    };
    const response = await getRequest<string>('notifications/v3/export', { params });
    return { data: response.data, error: null };
  } catch (error) {
    if (!axios.isAxiosError(error)) throw error;
    return { data: null, error: parseAxiosError(error) };
  }
}

// Grouping these functions for a namespace export, rather than named exports,
// to avoid naming collisions with action creators
export default {
  acknowledgeNotification,
  acknowledgeNotifications,
  exportNotifications,
  fetch7DayUnacknowledgedNotifications,
  fetchPaginatedNotifications
};